import { Modal, Spin, Tooltip } from 'antd';
import React, { useEffect, useRef, useState } from 'react';
import styled from 'styled-components';
import {
  maxMobile,
  primaryFont,
  primaryHeadingColor, secondaryFont, wMBold,
  wMedium,
} from 'Utils/utilsStyle';
import { actions } from 'Slice/DocumentHubSlice';

import {
  IsMobile,
  convertToFile, getFileSize,
} from 'Utils/UtilityFunctions';
import { useDispatch, useSelector } from 'react-redux';
import {
  selectBatchSize,
  selectUploadDocumentStatus, selectUploadDocuments,
  selectValidateLoading, selectuploadingFileError,
} from 'Selector/DocumentHubSelector';
import classNames from 'classnames';
import CrossCircle from '../assets/svgIcons/CrossCircle';
import InputWithRef from '../components/InputWithRef';

const DFileModal = styled(Modal)`
.ant-modal-content {
  .ant-modal-body {
    padding-top: 32px !important;
    padding-bottom: 0px !important;
    padding: 32px;
    @media all and (max-width: ${maxMobile}) {
      padding-right: 20px;
      padding-left: 20px;
    }
    .popup-title {
      color: ${primaryHeadingColor};
      font-family: ${secondaryFont};
      font-size: 32px;
      font-weight: ${wMBold};
      line-height: 47px;
      letter-spacing: -1px;
      margin-bottom: 12px;
      @media all and (max-width: ${maxMobile}) {
        font-size: 25px;
        line-height: 30px;
      }
    }
    .input-with-ref-style-wrapper {
      position: relative;
      .auth-input-label {
        margin: 0px;
        position: absolute;
        top: 50px;
        .error-msg  {
          display: flex;
          align-items: center;
          color: #F43C45;
          font-family: ${primaryFont};
          font-style: normal;
          font-weight: ${wMedium};
          .info-icon-red {
            width: 12px;
            height: 12px;
            margin-right: 5px;
          }
          .text-for-error {
            line-height: normal;
          }
        }
      }
      .auth-input {
        padding: 7px 18px;
      }
      .auth-input.error {
        border: 1.3px solid #F43C45 !important;
      }
    }
  }
  .ant-modal-footer {
    padding: 44px 32px 32px;
    .ant-btn  {
      border-radius: 23px;
      font-family: ${primaryFont};
      min-width: 101px;
      font-size: 14px;
      line-height: 23px;
      padding: 4px 15px;      
      margin: 0px 4px;
      max-height: 33px;
      &.ant-btn-default {
        .triggers-bullet-tooltip {
          .ant-tooltip {
            left: -60px !important;
            top: -44px !important;
            @media all and (max-width: ${maxMobile}) {
              left: -10px !important;
            }
          }
        }
      }
      .ant-spin {
        height: 18px;
        .ant-spin-dot {
          font-size: 16px;
          .ant-spin-dot-item {
            background-color: #fff;
            width: 8px;
            height: 8px;
          }
        }
      }
    }
  }
}  
`;

export const getFileType = (type: string, name?: string) => {
  if (type.includes('/plain')) {
    return 'txt';
  }
  if (type.includes('.document') || name?.includes('.docx')) {
    return 'docx';
  }
  if (name?.includes('.doc')) {
    return 'doc';
  }
  if (type.includes('.presentation') || name?.includes('.pptx')) {
    return 'pptx';
  }
  if (type.includes('.svg') || name?.includes('.svg')) {
    return 'svg';
  }
  if (type.includes('.jpeg') || name?.includes('.jpeg')) {
    return 'jpeg';
  }
  if (type.includes('.jpg') || name?.includes('.jpg')) {
    return 'jpg';
  }
  if (type.includes('.png') || name?.includes('.png')) {
    return 'png';
  }
  if (name?.includes('.xlsx')) {
    return 'xlsx';
  }
  if (type.includes('.xls') || name?.includes('.xls')) {
    return 'xls';
  }
  if (type.includes('.csv') || name?.includes('.csv')) {
    return 'csv';
  }
  return 'pdf';
};

const DuplicateFilePopup = function DuplicateFilePopup() {
  const nameRef = useRef<HTMLInputElement>(null);
  const isMobile = IsMobile();
  const dispatch = useDispatch();
  const uploadDocuments = useSelector(selectUploadDocuments);
  const ValidateLoading = useSelector(selectValidateLoading);
  const uploadDocumentStatus = useSelector(selectUploadDocumentStatus);
  const [error, setError] = useState(!uploadDocumentStatus);
  const uploadingFileError = useSelector(selectuploadingFileError);
  const batchSize = useSelector(selectBatchSize);
  const [isDisabled, setIsDisabled] = useState(true);
  const [renameFileValue, setRenameFileValue] = useState((uploadDocuments[0].name)?.replace(/\.[^.]+$/, ''));

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { value } = e.target;
    e.preventDefault();
    setError(false);
    setRenameFileValue(value);
    setIsDisabled(value.trim() === '');
  };

  useEffect(() => {
    if (!ValidateLoading && uploadingFileError) {
      setError(!uploadDocumentStatus);
    }
  }, [ValidateLoading]);

  const handleOk = () => {
    const file = uploadDocuments[0];
    if (file) {
      const reader = new FileReader();
      reader.readAsDataURL(file);
      reader.onloadend = () => {
        const convertedFile = convertToFile(reader?.result as string, `${renameFileValue}.${getFileType(file?.type, file?.name)}`);
        const fileWithUid = Object.assign(convertedFile, {
          uid: file.uid,
        });
        dispatch(actions.updateFileName(fileWithUid));
      };
    }
  };
  const handleCancel = () => {
    const file = uploadDocuments[0];
    dispatch(actions.setUploadDocumentError(false));
    dispatch(actions
      .setFailDocuments({
        uid: file.uid,
        name: file.name,
        size: getFileSize(file.size),
        failReason: 'Cancelled upload',
      }));
    dispatch(actions.removeUploadDocument());
  };

  const handleClose = () => {

  };

  return (
    <DFileModal
      open
      centered
      okButtonProps={{ className: classNames({ disabled: error || isDisabled }) }}
      width={isMobile ? 320 : 528}
      closeIcon={<div className="cross-icon-x" onClick={handleClose}><CrossCircle /></div>}
      getContainer=""
      maskClosable={false}
      wrapClassName="duplicate-file-popups"
      onCancel={() => handleCancel()}
      onOk={() => handleOk()}
      okText={ValidateLoading ? <Spin /> : 'Confirm'}
      cancelText={batchSize > 1 ? (
        <Tooltip
          arrow={false}
          placement={isMobile ? 'topLeft' : 'top'}
          getPopupContainer={(e: HTMLElement) => e}
          className="triggers-bullet-tooltip btn-tooltip"
          color="#fff"
          title="Exclude this file from uploading"
        >
          <span>
            Cancel
          </span>
        </Tooltip>
      ) : (
        <span>
          Cancel
        </span>
      )}
      closable
    >
      <h1 className="popup-title">Duplicate file exists</h1>
      <InputWithRef
        inputLabel=""
        inputName=""
        isDisabled={false}
        inputType="text"
        inputPlaceholder="Enter Full Name…"
        inputValue={renameFileValue}
        onInputChange={(e: React.ChangeEvent<HTMLInputElement>) => handleChange(e)}
        onInputBlur={() => { }}
        error={error ? 'File name already exists, rename to continue uploading this file.' : ''}
        ref={nameRef}
        inputClassName="name"
        isPassword={false}
        onInputTogglePassword={() => { }}
        autocomplete=""
        errorIcon
        maxLength={60}
      />
    </DFileModal>
  );
};
export default DuplicateFilePopup;
